/**
 * Admin-themed wrappers around the shared async-listbox interaction contract.
 * Admin and Manager share keyboard, virtual-cursor, and async-result behavior;
 * each app supplies its own visual-system classes.
 */
import {
  Listbox as SharedListbox,
  ListboxMessage as SharedListboxMessage,
  ListboxOption as SharedListboxOption,
  ListboxSearch as SharedListboxSearch,
  optionDomId,
  useListboxId,
} from '@speaking-coach/roster-list'
import type { ComponentProps } from 'react'

import { cn } from './cn'

export { optionDomId, useListboxId }

export function ListboxSearch(
  props: ComponentProps<typeof SharedListboxSearch>,
): JSX.Element {
  return (
    <SharedListboxSearch
      {...props}
      classes={{
        root: 'relative',
        icon:
          'pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-faint',
        input:
          'h-10 w-full rounded-none border-0 bg-transparent pl-9 pr-3 text-body text-text-primary ' +
          'placeholder:text-text-secondary focus-visible:outline-none focus-visible:ring-0',
        ...props.classes,
      }}
    />
  )
}

export function Listbox({
  className,
  ...props
}: ComponentProps<typeof SharedListbox>): JSX.Element {
  return (
    <SharedListbox
      className={cn('max-h-64 overflow-y-auto overflow-x-hidden p-1', className)}
      {...props}
    />
  )
}

export function ListboxOption({
  className,
  active,
  disabled = false,
  ...props
}: ComponentProps<typeof SharedListboxOption>): JSX.Element {
  return (
    <SharedListboxOption
      {...props}
      active={active}
      disabled={disabled}
      className={cn(
        'relative flex select-none items-center gap-2 rounded-token-input px-2 py-2 text-body pointer-coarse:min-h-11',
        disabled
          ? 'cursor-not-allowed text-text-secondary opacity-70'
          : 'cursor-pointer',
        active && 'bg-surface-card-hover',
        className,
      )}
    />
  )
}

export function ListboxMessage({
  className,
  ...props
}: ComponentProps<typeof SharedListboxMessage>): JSX.Element {
  return (
    <SharedListboxMessage
      className={cn('px-3 py-4 text-center text-body text-text-secondary', className)}
      {...props}
    />
  )
}
